/**
 * SupplierInvoiceEditModal — bearbeitet Domain-Felder einer Lieferanten-Rechnung.
 *
 * Use-Case: Faelligkeit, Betrag oder Kategorie falsch erfasst — Korrektur ohne
 * neuen Upload. Backend: PUT /api/v1/properties/{propertyId}/supplier-invoices/{id}.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  Textarea,
} from '@/shared/components';
import {
  toApiError,
  useApi,
  type ExpenseCategory,
  type SupplierInvoiceResponse,
  type UpdateSupplierInvoiceRequest,
} from '@/shared/api';
import { EXPENSE_CATEGORY_LABELS as CATEGORY_LABELS } from '../expenseCategoryLabels';
import { EXPENSE_CATEGORIES } from './supplierInvoicesUploadModel';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  invoice: SupplierInvoiceResponse | null;
}

export function SupplierInvoiceEditModal({ open, onOpenChange, propertyId, invoice }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [issuedOn, setIssuedOn] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [category, setCategory] = useState<ExpenseCategory | ''>('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && invoice) {
      setSupplier(invoice.supplier);
      setInvoiceNumber(invoice.invoiceNumber ?? '');
      setAmount(String(invoice.amount));
      setCurrency(invoice.currency);
      setIssuedOn(invoice.issuedOn);
      setDueOn(invoice.dueOn ?? '');
      setCategory(invoice.category);
      setNotes(invoice.notes ?? '');
    }
  }, [open, invoice]);

  const update = useMutation({
    mutationFn: (body: UpdateSupplierInvoiceRequest) => {
      if (!invoice) return Promise.reject(new Error('Keine Rechnung ausgewählt'));
      return api.supplierInvoices.update(propertyId, invoice.id, body);
    },
    onSuccess: () => {
      toast.success('Rechnung aktualisiert');
      void qc.invalidateQueries({ queryKey: ['supplierInvoices', propertyId] });
      void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
      onOpenChange(false);
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  function submit() {
    if (!invoice) return;
    if (!supplier.trim() || !amount.trim() || !issuedOn || !category) {
      toast.error('Lieferant, Betrag, Datum und Kategorie sind Pflichtfelder.');
      return;
    }
    update.mutate({
      supplier: supplier.trim(),
      invoiceNumber: invoiceNumber.trim() || null,
      amount: Number(amount.replace(',', '.')),
      currency: currency || 'EUR',
      issuedOn,
      dueOn: dueOn || null,
      category: category as ExpenseCategory,
      notes: notes.trim() || null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rechnung bearbeiten</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Lieferant" required>
              <Input
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder="Vodafone"
              />
            </FormField>
            <FormField label="Rechnungsnummer">
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="RG-2026-001"
              />
            </FormField>
          </div>
          <FormField label="Kategorie" required>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory | '')}
            >
              <option value="">—</option>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c] ?? c}
                </option>
              ))}
            </Select>
          </FormField>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Betrag" required className="col-span-2">
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="120,00"
              />
            </FormField>
            <FormField label="Währung">
              <Input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Beleg-Datum" required>
              <Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
            </FormField>
            <FormField label="Fällig am">
              <Input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Notizen">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button variant="brand" onClick={submit} loading={update.isPending} disabled={!invoice}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
